import { Fragment, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useNavigate, useLocation } from 'react-router-dom';
//constants
import { pagesEnum } from 'constants/ui.constants';
// @mui
import { Stack } from '@mui/system';
// components
import { Page } from 'theme/elements/Page';
import { CenteredNarrowContent } from 'theme/elements/Content';
import CancelDialog from 'components/CancelDialog';
// sections
import Stepper from 'theme/elements/Stepper';
import CreateHeader from 'sections/beneficiary/upsert/CreateHeader';
import Step1 from './steps/step1';
import Step2 from './steps/step2';
import Step3 from './steps/step3';
import Step4 from './steps/step4';
// reducers
import { useAppSelector, useAppDispatch } from 'hooks/redux.hooks';
import {
	toggleUpserting,
	getActiveStep,
	getSteps,
	intializeBeneficiary,
	getBeneficiaryById,
	getNonePendingBeneficiaries,
	isLoading,
	getNewBeneficiary,
	setActiveStep,
	setPaymentCategoryOptions,
} from 'state/reducers/beneficiary.reducers';
import WaitingMessage from 'components/WaitingMessage';
import { deleteBeneficiary, getPaymentsOptions, getPaymentCategories } from 'services/beneficiary.services';
import { Beneficiary, BeneficiaryStatus, beneficiarySteps } from 'constants/beneficiary.constants';
import {
	cancelDialogProps,
	getHistory,
	getMobile,
	getUi,
	ifPageChange,
	setCheckBeforeNavigate,
	togglePage,
} from 'state/reducers/ui.reducers';
import { selectTranslations } from 'state/reducers/translation.reducers';
import MobileNavbar from 'sections/layout/MobileNavbar';
import { getUrlBack } from 'theme/elements/Headers';
import { translatedCancelPopup } from 'utils';

export default function BeneficiarySteps() {
	const navigate = useNavigate();
	const t = useAppSelector(selectTranslations);
	const location = useLocation();
	const dispatch = useAppDispatch();
	const ifLoading = useAppSelector(isLoading);
	const [openCancelDialog, setOpenCancelDialog] = useState(false);
	const activeStep = useAppSelector(getActiveStep);
	const steps = useAppSelector(getSteps);
	const isMobile = useAppSelector(getMobile);
	//get location
	const paths = location.pathname.split('/');
	const lastPath = paths[paths.length - 1];
	const found = useAppSelector(getBeneficiaryById(lastPath));
	const beneficiaries = useAppSelector(getNonePendingBeneficiaries);
	const newBeneficiary: Beneficiary = useAppSelector(getNewBeneficiary);
	const ui = useAppSelector(getUi);
	const isLeavingPage = useAppSelector(ifPageChange);
	const history = useAppSelector(getHistory);
	const urlback = getUrlBack(location.pathname, history[history.length - 1]);
	const dialogProps = useAppSelector(cancelDialogProps);
	const dialogPropsTranslated = useMemo(() => translatedCancelPopup(dialogProps), [dialogProps]);
	const [isPaymentOptionsPreloaded, setIsPaymentOptionsPreloaded] = useState<boolean>(false);
	const [isCountryChanged, setIsCountryChanged] = useState(false);
	const stepProps = {
		handleCancel: () => setOpenCancelDialog(true),
		isCountryChanged: isCountryChanged,
		setIsCountryChanged: setIsCountryChanged,
	};
	// Prefetch payment categories early for both create and edit flows
	useEffect(() => {
		getPaymentCategories()
			.then((cats) => dispatch(setPaymentCategoryOptions(cats || [])))
			.catch(() => {});
	}, []);

	useEffect(() => {
		if (!_.isEqual(lastPath, 'new')) {
			if (found) {
				if (
					![
						BeneficiaryStatus.Approved,
						BeneficiaryStatus.ApprovedWithRisk,
						BeneficiaryStatus.Suspended,
						BeneficiaryStatus.DeclinedForRepair,
					].includes(found?.status)
				) {
					dispatch(intializeBeneficiary(found));
					if (found.status !== BeneficiaryStatus.PendingBankAccountDetails) {
						dispatch(getPaymentsOptions(found, false, false));
					}
					if (found.status === BeneficiaryStatus.PendingSigning) {
						dispatch(setActiveStep(beneficiarySteps.REVIEW));
					}
				} else {
					dispatch(getPaymentsOptions(found, false, false));
					setIsPaymentOptionsPreloaded(true);
				}
			} else {
				navigate('/404');
			}
		}
	}, [found?.id]);

	useEffect(() => {
		let flag = activeStep > 0 && activeStep < 3 ? true : false;
		flag = _.isEqual(lastPath, 'new') && _.has(newBeneficiary, 'id') ? true : flag;
		dispatch(setCheckBeforeNavigate(flag));
		window.scrollTo(0, 0);
	}, [activeStep]);

	useEffect(() => {
		if (isLeavingPage && ui.checkBeforeNavigate) {
			activeStep !== 3 ? setOpenCancelDialog(true) : navigate(`/${pagesEnum.BENEFICIARY}`);
		}
	}, [isLeavingPage]);
	return (
		<Fragment>
			<MobileNavbar
				handleBack={() => {
					if (activeStep > 0 && activeStep < 3) {
						dispatch(setActiveStep(_.values(beneficiarySteps)[activeStep - 1]));
					} else {
						dispatch(togglePage(urlback));
					}
				}}
				handleCancel={() => {
					setOpenCancelDialog(true);
				}}
				noCancel={_.includes([0, 3], activeStep)}
			/>
			{ifLoading ? (
				<WaitingMessage title="getting there..." imageKey="page.beneficiary.create.loading.icon" />
			) : (
				<Page>
					<Stack spacing={{ sm: 5, xs: 1 }}>
						{!isMobile && <CreateHeader isEdit={found ? true : false} />}
						<Stack spacing={{ sm: 5, xs: 1 }}>
							<Stepper
								activeStep={activeStep}
								total={steps}
								stepsPrefix={'page.beneficiary.create.step'}
							/>
							<CenteredNarrowContent>
								<Fragment>
									{_.isEqual(activeStep, 0) && <Step1 {...stepProps} />}
									{_.isEqual(activeStep, 1) && (
										<Step2 {...stepProps} isPaymentOptionsPreloaded={isPaymentOptionsPreloaded} />
									)}
									{_.isEqual(activeStep, 2) && (
										<Step3 {...stepProps} isNew={_.isEqual(lastPath, 'new')} />
									)}
									{_.isEqual(activeStep, 3) && <Step4 beneficiaries={beneficiaries} />}
								</Fragment>
							</CenteredNarrowContent>
						</Stack>
						<CancelDialog
							open={openCancelDialog}
							{...dialogPropsTranslated}
							handleContinue={() => {
								setOpenCancelDialog(false);
								if (isLeavingPage) {
									dispatch(togglePage());
								}
							}}
							handleCancel={() => {
								if (newBeneficiary?.id) {
									dispatch(deleteBeneficiary(newBeneficiary.id, true));
								}
								setOpenCancelDialog(false);
								dispatch(toggleUpserting(false));
								if (!isLeavingPage) {
									navigate(`/${pagesEnum.BENEFICIARY}`);
								} else {
									navigate(ui.to);
								}
							}}
						/>
					</Stack>
				</Page>
			)}
		</Fragment>
	);
}
